import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, Mail, MapPin, Youtube } from "lucide-react";
import { PageHeader, SectionLabel } from "@/components/shared";
import { Reveal } from "@/components/motion/Reveal";
import Newsletter from "@/components/Newsletter";
import { postContact } from "@/lib/api";
import { ASSETS, SITE } from "@/lib/assets";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setLoading(true);
    try {
      const res = await postContact(form);
      toast.success(res.message || "Message sent!");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-white/20 focus:border-wutn py-3 text-white placeholder:text-zinc-500 outline-none transition-colors";

  return (
    <div data-testid="contact-page">
      <PageHeader
        testId="contact-header"
        label="Say Hello"
        title={<>Get In <span className="text-wutn">Touch</span></>}
        subtitle="Questions, invitations, partnership, or just to say hi — we'd love to hear from you."
        image={ASSETS.founderWhiteWide}
      />

      <section className="bg-black py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <SectionLabel>Reach Us</SectionLabel>
            <h2 className="mt-5 font-display uppercase text-4xl md:text-5xl leading-[0.9]">Let&apos;s <span className="text-wutn">Talk</span></h2>
            <p className="mt-6 text-zinc-400 text-lg">Fill out the form and a member of our team will get back to you. For prayer, use our dedicated prayer page.</p>

            <div className="mt-10 space-y-5">
              <a href={`mailto:${SITE.email}`} data-testid="contact-email-link" className="flex items-center gap-4 group">
                <span className="inline-flex h-12 w-12 items-center justify-center bg-wutn/10 text-wutn group-hover:bg-wutn group-hover:text-white transition-colors"><Mail className="h-5 w-5" /></span>
                <span className="text-zinc-300 group-hover:text-white transition-colors">{SITE.email}</span>
              </a>
              <div className="flex items-center gap-4">
                <span className="inline-flex h-12 w-12 items-center justify-center bg-wutn/10 text-wutn"><MapPin className="h-5 w-5" /></span>
                <span className="text-zinc-300">{SITE.location}</span>
              </div>
              <a href={SITE.youtube} target="_blank" rel="noreferrer" data-testid="contact-youtube-link" className="flex items-center gap-4 group">
                <span className="inline-flex h-12 w-12 items-center justify-center bg-wutn/10 text-wutn group-hover:bg-wutn group-hover:text-white transition-colors"><Youtube className="h-5 w-5" /></span>
                <span className="text-zinc-300 group-hover:text-white transition-colors">@WakeUpTheNation</span>
              </a>
            </div>

            <div className="mt-12 bg-[#0A0A0A] border border-white/10 p-6">
              <h4 className="font-cond uppercase tracking-wider text-white text-sm mb-2">Join The Newsletter</h4>
              <p className="text-sm text-zinc-400 mb-4">New sermons, events, and updates — straight to your inbox.</p>
              <Newsletter />
            </div>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal>
              <form onSubmit={submit} data-testid="contact-form" className="bg-[#0A0A0A] border border-white/10 p-8 md:p-10 space-y-7">
                <div className="grid sm:grid-cols-2 gap-7">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-zinc-500">Name *</label>
                    <input required value={form.name} onChange={set("name")} data-testid="contact-name" className={inputCls} placeholder="Your name" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-zinc-500">Email *</label>
                    <input type="email" required value={form.email} onChange={set("email")} data-testid="contact-email" className={inputCls} placeholder="you@email.com" />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-500">Subject</label>
                  <input value={form.subject} onChange={set("subject")} data-testid="contact-subject" className={inputCls} placeholder="What's this about?" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-500">Message *</label>
                  <textarea required rows={5} value={form.message} onChange={set("message")} data-testid="contact-message" className={`${inputCls} resize-none`} placeholder="Tell us more..." />
                </div>
                <button type="submit" disabled={loading} data-testid="contact-submit" className="w-full inline-flex items-center justify-center gap-2 bg-wutn hover:bg-wutn-dark disabled:opacity-60 text-white px-6 py-4 font-cond uppercase tracking-wider text-sm transition-colors">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send Message
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
